import { DateFormatter } from '@repo/toolkit/date'
import Checkbox from '@repo/ui/checkbox'
import { DateRangeInput } from '@repo/ui/date-range-input'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getHowDidYouHearAboutUs } from '../../api/how-did-you-hear-about-us/get-how-did-you-hear-about-us'
import {
  EmailValidityStatusEnum,
  emailValidityStatusEnumMapping
} from '../../lib/types/enums/information-requests/email-validity-status.enum'
import {
  OrigineEnum,
  origineEnumMapping
} from '../../lib/types/enums/information-requests/origin.enum'
import {
  PdfTypeEnum,
  pdfTypeEnumMapping
} from '../../lib/types/enums/information-requests/pdf-type.enum'
import {
  ReceiveBrochureEnum,
  receiveBrochureEnumMapping
} from '../../lib/types/enums/information-requests/recieve-brochure.enum'
import { InformationRequestFilters } from '../../lib/types/models/information-request/information-request'

type Props = {
  filter?: InformationRequestFilters
  onFilterChange: (filter: InformationRequestFilters) => void
}

const InformationRequestFilter: React.FC<Props> = ({ filter, onFilterChange }) => {
  const { data: howDidYouHearAboutUsOptions } = useQuery({
    queryKey: ['how-did-you-hear-about-us'],
    queryFn: () => getHowDidYouHearAboutUs()
  })
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Date de la demande</Typography>
        <DateRangeInput
          key={`${filter?.createdAt?.startDate ?? 'start-date'}-${filter?.createdAt?.endDate ?? 'end-date'}-createdAt`}
          value={
            filter?.createdAt?.startDate && filter.createdAt?.endDate
              ? {
                  from: DateFormatter.toDate(filter.createdAt.startDate),
                  to: DateFormatter.toDate(filter.createdAt.endDate)
                }
              : undefined
          }
          onClearDate={() => onFilterChange({ ...filter, createdAt: undefined })}
          onDateRangeChange={(dateRange) => {
            if (dateRange?.from && dateRange?.to)
              onFilterChange({
                ...filter,
                createdAt: {
                  startDate: DateFormatter.toDateFormatted(dateRange?.from),
                  endDate: DateFormatter.toDateFormatted(dateRange?.to)
                }
              })
          }}
        />
      </div>
      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Date d'impression</Typography>
        <DateRangeInput
          key={`${filter?.printedAt?.startDate ?? 'start-date-print'}-${filter?.printedAt?.endDate ?? 'end-date-print'}-printedAt`}
          value={
            filter?.printedAt?.startDate && filter?.printedAt?.endDate
              ? {
                  from: DateFormatter.toDate(filter.printedAt.startDate),
                  to: DateFormatter.toDate(filter.printedAt.endDate)
                }
              : undefined
          }
          onClearDate={() => {
            onFilterChange({ ...filter, printedAt: undefined })
          }}
          onDateRangeChange={(dateRange) => {
            if (dateRange?.from && dateRange?.to)
              onFilterChange({
                ...filter,
                printedAt: {
                  startDate: DateFormatter.toDateFormatted(dateRange?.from),
                  endDate: DateFormatter.toDateFormatted(dateRange?.to)
                }
              })
          }}
        />
      </div>
      <div className="flex flex-col gap-2.5">
        <Typography variant="tag">Type d'impression</Typography>
        <div className="flex flex-col gap-1">
          {Object.values(PdfTypeEnum).map((type) => (
            <Checkbox
              key={type}
              checked={filter?.pdfTypes?.includes(type)}
              label={pdfTypeEnumMapping[type].label}
              onChange={(checked) =>
                onFilterChange({
                  ...filter,
                  pdfTypes: checked
                    ? [...(filter?.pdfTypes ?? []), type]
                    : filter?.pdfTypes?.filter((t) => t !== type)
                })
              }
            />
          ))}
        </div>

        <div className="flex flex-col gap-2.5">
          <Typography variant="tag">Choix du format</Typography>
          <div className="flex flex-col gap-1">
            {Object.values(ReceiveBrochureEnum).map((type) => (
              <Checkbox
                key={type}
                checked={filter?.receiveBrochureBy?.includes(type)}
                label={receiveBrochureEnumMapping[type]}
                onChange={(checked) =>
                  onFilterChange({
                    ...filter,
                    receiveBrochureBy: checked
                      ? [...(filter?.receiveBrochureBy ?? []), type]
                      : filter?.receiveBrochureBy?.filter((t) => t !== type)
                  })
                }
              />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <Typography variant="tag">Statut d'impression</Typography>
          <div className="flex flex-row gap-5">
            <RadioInput
              checked={filter?.isPrinted === undefined}
              labelTextVariant="secondary"
              label={'Les deux'}
              onCheck={() => onFilterChange({ ...filter, isPrinted: undefined })}
            />
            <RadioInput
              labelTextVariant="secondary"
              checked={filter?.isPrinted === true}
              label={'Imprimé'}
              onCheck={() => onFilterChange({ ...filter, isPrinted: true })}
            />
            <RadioInput
              checked={filter?.isPrinted === false}
              labelTextVariant="secondary"
              label={'Non imprimé'}
              onCheck={() => onFilterChange({ ...filter, isPrinted: false })}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <Typography variant="tag">Comment avez-vous connu LEC ?</Typography>
          <div className="flex flex-col gap-1">
            {howDidYouHearAboutUsOptions?.data.map((data) => (
              <Checkbox
                key={data.id}
                checked={filter?.howDidYouHearAboutUsIds?.includes(data.id)}
                label={data.name}
                onChange={(checked) =>
                  onFilterChange({
                    ...filter,
                    howDidYouHearAboutUsIds: checked
                      ? [...(filter?.howDidYouHearAboutUsIds ?? []), data.id]
                      : filter?.howDidYouHearAboutUsIds?.filter((t) => t !== data.id)
                  })
                }
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2.5">
          <Typography variant="tag">Source</Typography>
          <div className="flex flex-col gap-1">
            {Object.values(OrigineEnum).map((type) => (
              <Checkbox
                key={type}
                checked={filter?.origins?.includes(type)}
                label={origineEnumMapping[type].label}
                onChange={(checked) =>
                  onFilterChange({
                    ...filter,
                    origins: checked
                      ? [...(filter?.origins ?? []), type]
                      : filter?.origins?.filter((t) => t !== type)
                  })
                }
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2.5">
          <Typography variant="tag">Lieu de résidence</Typography>
          <div className="flex flex-row gap-5">
            <RadioInput
              checked={filter?.inFrance === undefined}
              label={'Les deux'}
              labelTextVariant="secondary"
              onCheck={() => onFilterChange({ ...filter, inFrance: undefined })}
            />
            <RadioInput
              checked={filter?.inFrance === true}
              label={'En France'}
              labelTextVariant="secondary"
              onCheck={() => onFilterChange({ ...filter, inFrance: true })}
            />
            <RadioInput
              checked={filter?.inFrance === false}
              label={'Hors de France'}
              labelTextVariant="secondary"
              onCheck={() => onFilterChange({ ...filter, inFrance: false })}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <Typography variant="tag">Statut de validité de l'email</Typography>
          <div className="flex flex-row gap-5">
            {Object.values(EmailValidityStatusEnum).map((type) => (
              <RadioInput
                labelTextVariant="secondary"
                key={type}
                checked={filter?.emailValidityStatus === type}
                label={emailValidityStatusEnumMapping[type].label}
                onCheck={() => onFilterChange({ ...filter, emailValidityStatus: type })}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default InformationRequestFilter
